import type { FetchOptions } from 'ofetch'

export const metaMethodKey = Symbol.for('meta-method-data')
export const metaMethodTypeKey = Symbol.for('meta-method-type-data')

export type MethodTypes
  = | 'post'
    | 'patch'
    | 'get'
    | 'put'
    | 'delete'
    | '$post'
    | '$patch'
    | '$get'

export type PathNotation = string | ((...args: any[]) => string)

export type MethodCfgInner = {
  path: PathNotation
  options?:
    | FetchOptions<'json' | 'stream'>
    | ((...args: unknown[]) => FetchOptions<'json' | 'stream'>)
}

export type MethodTypeCfgInner = {
  type: PathNotation
  options?:
    | FetchOptions<'json' | 'stream'>
    | ((...args: unknown[]) => FetchOptions<'json' | 'stream'>)
}

export type MethodCfg = MethodCfgInner & {
  type: MethodTypes
  path: string
  methodKey: string
}

export function resolveMethodMeta(obj: object): MethodCfg {
  return Reflect.getOwnMetadata(metaMethodKey, obj)
}

export function resolveMethodTypeMeta(obj: object): MethodCfg {
  return Reflect.getOwnMetadata(metaMethodTypeKey, obj)
}

const makeMethod = (type: MethodTypes) => {
  /**
   * path or config
   */
  return function (cfg: PathNotation | MethodCfgInner) {
    return function (
      _target: unknown,
      methodKey: string,
      _descriptor: PropertyDescriptor,
    ) {
      const originalFunction = _descriptor.value

      _descriptor.value = function (...args: unknown[]) {
        const config: MethodCfgInner = Object.assign(
          {},
          typeof cfg !== 'object' ? { path: cfg } : cfg,
        )
        const path
          = typeof config.path === 'function'
            ? config.path(...args)
            : config.path
        config.options
          = typeof config.options === 'function'
            ? config.options(...args)
            : config.options

        Reflect.defineMetadata(
          metaMethodKey,
          {
            ...config,
            path,
            type: type.toLowerCase(),
            methodKey,
          } as MethodCfg,
          this,
        )

        return Reflect.apply(originalFunction, this, args)
      }
      return _descriptor
    }
  }
}

export const $POST = makeMethod('$post')
export const $GET = makeMethod('$get')
export const $PATCH = makeMethod('$patch')
export const POST = makeMethod('post')
export const PUT = makeMethod('put')
export const GET = makeMethod('get')
export const PATCH = makeMethod('patch')
export const DELETE = makeMethod('delete')

export function IsApiGateWay({ prefix }: { prefix: string }) {
  return function <
    T extends { new (...args: unknown[]): object & { PREFIX: string } },
  >(constructor: T) {
    constructor.prototype.PREFIX = prefix
    return constructor
  }
}
